import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  useTheme
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Route as RouteIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import StreamGrid from './StreamGrid';
import streamsApi from '../../api/streamsApi';
import { useAuth } from '../../context/AuthContext';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

/**
 * LiveViewPage Component
 * Main page for viewing live streams from ENViS devices
 * Supports organization-wide, network-specific, and section-specific views
 */
const LiveViewPage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // State management
  const [viewMode, setViewMode] = useState('organization'); // 'organization', 'network', 'section'
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [devices, setDevices] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streamStatus, setStreamStatus] = useState(null);

  // Auto-refresh interval (30 seconds)
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshInterval = 30000; // 30 seconds

  // Load streams based on current view mode and filters
  const loadStreams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate required parameters based on view mode
      if (viewMode === 'network' && !selectedNetwork) {
        setError('Please select a network to view streams');
        setDevices([]);
        setLoading(false);
        return;
      }
      
      if (viewMode === 'section' && !selectedSection) {
        setError('Please select a section to view streams');
        setDevices([]);
        setLoading(false);
        return;
      }
      
      const params = {
        mode: viewMode,
        active_only: true
      };
      
      if (viewMode === 'network' && selectedNetwork) {
        params.network_id = selectedNetwork;
      } else if (viewMode === 'section' && selectedSection) {
        params.section_id = selectedSection;
      }
      
      const response = await streamsApi.getStreams(params);
      setDevices(response.devices || []);
      
      // Load stream status (pass device IDs)
      if (response.devices && response.devices.length > 0) {
        const deviceIds = response.devices.map(d => d.id).join(',');
        const statusResponse = await streamsApi.getStreamStatus({ device_ids: deviceIds });
        setStreamStatus(statusResponse);
      } else {
        setStreamStatus(null);
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error loading streams:', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedNetwork, selectedSection]);

  // Load available networks
  const loadNetworks = useCallback(async () => {
    try {
      const response = await streamsApi.getNetworksWithStreams();
      setNetworks(response);
    } catch (err) {
      console.error('Error loading networks:', err);
    }
  }, []);

  // Load available sections for selected network
  const loadSections = useCallback(async () => {
    if (selectedNetwork) {
      try {
        const response = await streamsApi.getSectionsWithStreams(selectedNetwork);
        setSections(response);
      } catch (err) {
        console.error('Error loading sections:', err);
        setSections([]);
      }
    } else {
      setSections([]);
    }
  }, [selectedNetwork]);

  // Initial data loading
  useEffect(() => {
    loadNetworks();
  }, [loadNetworks]);

  // Load streams when view mode or filters change
  useEffect(() => {
    loadStreams();
  }, [loadStreams]);

  // Load sections when network changes
  useEffect(() => {
    loadSections();
    // Reset section selection when network changes
    if (viewMode === 'section') {
      setSelectedSection('');
    }
  }, [loadSections, selectedNetwork, viewMode]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      loadStreams();
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [autoRefresh, loadStreams, refreshInterval]);

  // Handle view mode change
  const handleViewModeChange = (event) => {
    const newMode = event.target.value;
    setViewMode(newMode);
    setSelectedNetwork('');
    setSelectedSection('');
  };

  // Handle network selection
  const handleNetworkChange = (event) => {
    const networkId = event.target.value;
    setSelectedNetwork(networkId);
    if (viewMode === 'section') {
      setSelectedSection(''); // Reset section when network changes
    }
  };

  // Handle section selection
  const handleSectionChange = (event) => {
    setSelectedSection(event.target.value);
  };

  // Manual refresh
  const handleRefresh = () => {
    loadStreams();
  };

  // Get current view description
  const getViewDescription = () => {
    if (viewMode === 'organization') {
      return `All devices in ${user?.organization_name || 'your organization'}`;
    } else if (viewMode === 'network' && selectedNetwork) {
      const network = networks.find(n => n.id === selectedNetwork);
      return `Network: ${network?.name || 'Selected Network'}`;
    } else if (viewMode === 'section' && selectedSection) {
      const section = sections.find(s => s.id === selectedSection);
      return `Section: ${section?.name || 'Selected Section'}`;
    }
    return 'Select filters to view streams';
  };

  // Show loading while authentication is being determined
  if (!user) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Authenticating...
        </Typography>
      </Box>
    );
  }

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Live View
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Real-time ENViS device video streams monitoring
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <span>
            {devices.filter(d => d.stream_url).length > 0
              ? `${devices.filter(d => d.stream_url).length} active streams • ${devices.length} total devices`
              : `${devices.length} devices found • No active streams`}
          </span>
          {user?.organization_name && user.role !== 'admin' && (
            <>
              <span>•</span>
              <span>{user.organization_name}</span>
            </>
          )}
          {user?.role === 'admin' && (
            <>
              <span>•</span>
              <span style={{ fontWeight: 500, color: theme.palette.warning.main }}>
                System Admin View (All Organizations)
              </span>
            </>
          )}
          <span>•</span>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: autoRefresh ? theme.palette.success.main : theme.palette.warning.main,
                animation: autoRefresh ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }}
            />
            <span style={{ 
              color: autoRefresh ? theme.palette.success.main : theme.palette.warning.main,
              fontWeight: 500
            }}>
              {autoRefresh ? `Auto-refresh active (${refreshInterval/1000}s)` : 'Auto-refresh disabled'}
            </span>
          </Box>
        </Typography>
      </Box>

      {/* Controls and Actions */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* View Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>View Mode</InputLabel>
            <Select
              value={viewMode}
              onChange={handleViewModeChange}
              label="View Mode"
            >
              <MenuItem value="organization">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BusinessIcon fontSize="small" />
                  <span>Organization-wide</span>
                </Stack>
              </MenuItem>
              <MenuItem value="network">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <RouteIcon fontSize="small" />
                  <span>By Network</span>
                </Stack>
              </MenuItem>
              <MenuItem value="section">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationIcon fontSize="small" />
                  <span>By Section</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Network Selector */}
          {(viewMode === 'network' || viewMode === 'section') && (
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={loading}>
              <InputLabel>Network</InputLabel>
              <Select
                value={selectedNetwork}
                onChange={handleNetworkChange}
                label="Network"
              >
                {networks.map((network) => (
                  <MenuItem key={network.id} value={network.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <RouteIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">{network.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {network.code}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Section Selector */}
          {viewMode === 'section' && (
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={loading || !selectedNetwork}>
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                onChange={handleSectionChange}
                label="Section"
              >
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocationIcon fontSize="small" />
                      <Typography variant="body2">{section.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Action Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            icon={<VideocamIcon />}
            label={`${devices.filter(d => d.stream_url).length} Active`}
            color={devices.filter(d => d.stream_url).length > 0 ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
          
          <Tooltip title="Refresh Streams">
            <IconButton 
              onClick={handleRefresh} 
              color="primary"
              disabled={loading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Current View Description */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Current View:</strong> {getViewDescription()}
          {streamStatus && (
            <span> • Last updated: {new Date().toLocaleTimeString()}</span>
          )}
        </Typography>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading streams...
          </Typography>
        </Box>
      )}

      {/* Stream Grid */}
      {!loading && (
        <StreamGrid
          devices={devices}
          loading={loading}
          onRefresh={handleRefresh}
          error={error}
        />
      )}
    </Box>
    </DisciplineAccessControl>
  );
};

export default LiveViewPage;